import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useGlobalContext } from '../../../../context/GlobalContext';

import './AutoScrollStatus.scss';

// Long enough to read in a hurry, short enough to be gone before the next line is sung
export const SHOW_MS = 1500;

/**
 * "Auto Scroll on" or "off" in the middle of the screen for a moment each time it starts or stops,
 * while the plugins row, and the scroll control in it, are hidden. Smart Scroll can hold the page
 * still at the top, so without this a start can look like nothing happened.
 */
const AutoScrollStatus = ({ active }) => {
  const { t } = useTranslation();
  const { isScrolling } = useGlobalContext();
  // A count as well, so the same word shown twice in a row restarts its fade
  const [status, setStatus] = useState(null);
  const wasScrolling = useRef(isScrolling);

  // Only a change says anything: the page opening with scroll off isn't news
  useEffect(() => {
    if (wasScrolling.current === isScrolling) return;
    wasScrolling.current = isScrolling;

    if (active) setStatus((previous) => ({ on: isScrolling, count: (previous?.count || 0) + 1 }));
  }, [isScrolling, active]);

  useEffect(() => {
    if (!status) return undefined;

    const timer = setTimeout(() => setStatus(null), SHOW_MS);
    return () => clearTimeout(timer);
  }, [status]);

  if (!active || !status) return null;

  return (
    <div key={status.count} className={`AutoScrollStatus ${status.on ? 'is-on' : 'is-off'}`} role="status">
      {status.on ? t('view.autoScrollOn') : t('view.autoScrollOff')}
    </div>
  );
};

AutoScrollStatus.propTypes = {
  /** False while the plugins row is open, where the scroll control already shows its state */
  active: PropTypes.bool.isRequired,
};

export default AutoScrollStatus;
